import _extends from '@babel/runtime/helpers/extends';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
import { useState, useRef, useEffect } from 'react';
import Slider from 'react-slick';
import Arrow from './arrow/index.js';
import Style from './style.js';
import { isDefined } from '../../utils/isDefined/index.js';
import { jsx } from '@emotion/react';

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
var FIRST_INDEX = 0;
var BASE_SLIDER_CONFIG = {
  focusOnSelect: true,
  pauseOnHover: true,
  slidesToScroll: 1,
  variableWidth: true
}; // TODO-eslint: Reduce cognitive complexity of component

/* eslint-disable sonarjs/cognitive-complexity */

/**
 * Carousels should be used to provide valuable information or additional context on a page. One of the best examples of a Carousel is for product recommendations.
 *
 * The property `numCardsVisible` will define the width of the Carousel Container.
 *
 * An array of `Carousel.Card` must be used for the carousel content. It includes the base styles for the Card which may be extended as shown above.
 */

var Carousel = _ref => {
  var {
    autoplay = false,
    autoplaySpeed = 5000,
    bottomRightAlignedArrows = false,
    carouselType = 'primary',
    centerMode = true,
    children,
    hideArrows = false,
    hideDots = false,
    infinite = false,
    numCardsVisible
  } = _ref;

  var getLastIndex = () => {
    var numberSlides = children.length;
    return numberSlides - numCardsVisible;
  };

  var [currentIndex, setCurrentIndex] = useState(FIRST_INDEX);
  var [lastIndex, setLastIndex] = useState(getLastIndex);
  var hasUserInteractedRef = useRef(false);
  var timeoutIdRef = useRef(null);
  var slider = useRef(null);

  var shouldReplay = () => {
    var onLastCard = currentIndex === lastIndex;
    return autoplay && !infinite && onLastCard && !hasUserInteractedRef.current;
  };

  var replay = () => {
    timeoutIdRef.current = window.setTimeout(() => {
      if (slider.current) {
        slider.current.slickGoTo(FIRST_INDEX);
      }
    }, autoplaySpeed);
  };

  var updateLastIndex = () => {
    var updatedLastIndex = getLastIndex();

    if (lastIndex !== updatedLastIndex) {
      setLastIndex(updatedLastIndex);
    }

    if (slider.current && currentIndex > updatedLastIndex) {
      slider.current.slickGoTo(updatedLastIndex);
    }
  };

  useEffect(() => {
    if (shouldReplay()) {
      replay();
    }

    updateLastIndex();
  });

  var onCardChange = (_oldIndex, nextIndex) => {
    timeoutIdRef.current = null;
    setCurrentIndex(nextIndex);
  };

  var onUserInteraction = () => {
    if (isDefined(timeoutIdRef.current)) {
      clearTimeout(timeoutIdRef.current);
    }

    hasUserInteractedRef.current = true;

    if (slider.current) {
      slider.current.slickPause();
    }
  };

  var carouselSettings = _objectSpread(_objectSpread({}, BASE_SLIDER_CONFIG), {}, {
    arrows: !hideArrows,
    autoplay,
    autoplaySpeed,
    beforeChange: onCardChange,
    centerMode: numCardsVisible > 1 || infinite ? false : centerMode,
    dots: !hideDots && !bottomRightAlignedArrows,
    infinite,
    nextArrow: jsx(Arrow, {
      bottomRightAlignedArrows: bottomRightAlignedArrows,
      disabled: currentIndex === lastIndex && !infinite,
      next: true,
      onClick: onUserInteraction
    }),
    onSwipe: onUserInteraction,
    prevArrow: jsx(Arrow, {
      bottomRightAlignedArrows: bottomRightAlignedArrows,
      disabled: currentIndex === FIRST_INDEX && !infinite,
      onClick: onUserInteraction,
      prev: true
    }),
    slidesToShow: numCardsVisible
  });

  return jsx(Style.OuterContainer, {
    numCardsVisible: numCardsVisible
  }, jsx(Style.InnerContainer, {
    carouselType: carouselType,
    onClick: onUserInteraction
  }, jsx(Slider, _extends({
    ref: slider
  }, carouselSettings), children)));
};
/* eslint-enable sonarjs/cognitive-complexity */

Carousel.Card = Style.Card;

export { Carousel };
